/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  boxShadow,
  maxWidth,
  primaryColor,
  primaryFont,
  primaryHeadingColor,
  retina,
  secondaryFont,
  wMBold,
  wMedium,
} from 'Utils/utilsStyle';
import { useSelector, useDispatch } from 'react-redux';
import { actions as SRIActions } from 'Slice/SRISlice';
import { SRITabFilters } from 'Types/RequestBodies';
import {
  LeaderboardSummary,
  SRIFilters,
  SRITeamReadinessTeam,
  TeamReadinessAvgResponse,
} from 'Types/SRITypes';
import {
  // selectTeamReadinessFiltersLoading,
  selectTeamReadinessTeams,
  selectTeamReadinessSelectedRegion,
  selectedTeamSRI,
  selectTeamReadinessBeltsGraph,
  selectTeamReadinessBeltsLoading,
  selectOverviewFilter,
  selectTeamReadinessAvgGraph,
  selectTeamReadinessAvgLoading,
  selectTeamReadinessTeamRankLoading,
  selectTeamReadinessTeamRankData,
} from 'Selector/SRISelector';
import SkeletonCustom from 'Components/common/skeleton/SkeletonCustom';
import { useNavigate } from 'react-router-dom';
import BarLineChart from './BarLineChart';
import BeltChart from './BeltChart';
import TeamReadinessTableHeader from './TeamReadinessTableHeader';
import MultiLineChart from './MultiLineChart';
import ChartSpinner from './ChartSpinner';
import TeamDetails from './TeamDetails';
import TeamDeatilsCards from './TeamDeatilsCards';
import TeamReadinessTableBody from './TeamReadinessTableBody';

const Wrapper = styled.div`
  .active-c {
    color: ${primaryColor} !important;
  }
  .title {
    font-size: 25px;
    font-weight: ${wMBold};
    letter-spacing: -0.7px;
    font-family: ${secondaryFont};
    color: ${primaryHeadingColor};
    @media all and (max-width: ${maxWidth}) , ${retina} {
      font-size: 20px;
    }
  }
  .tabs-item {
    cursor: pointer;
  }
  .team-details {
    display: flex;
    margin-bottom: 10px;
    div {
      margin-right: 20px;
      font-size: 13px;
      font-family: ${primaryFont};
      font-weight: ${wMedium};
      letter-spacing: -0.33px;
      color: RGB(126, 126, 126);

      span {
        margin-left: 10px;
        margin-right: 5px;
        color: #1EC252;
        font-weight: ${wMBold};
      }
    }
  }
`;

const Overlay = styled.div`
  box-shadow: ${boxShadow};
  border-radius: 10px;
  background: #fff;
  padding: 24px 42px;
  margin-bottom: 24px;
`;

const TeamReadinessTab = function TeamReadinessTab() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const teamReadinessBeltsLoading: boolean = useSelector(selectTeamReadinessBeltsLoading);
  const teamReadinessBeltsGraph: LeaderboardSummary[] = useSelector(selectTeamReadinessBeltsGraph);
  const [isLoading, setIsLoading] = useState(true);
  const teamReadinessAvgLoading: boolean = useSelector(selectTeamReadinessAvgLoading);
  const teamReadinessAvgGraph: TeamReadinessAvgResponse = useSelector(selectTeamReadinessAvgGraph);
  const SRIFilter: SRIFilters = useSelector(selectOverviewFilter);
  const filterTeams = useSelector(selectTeamReadinessTeams);
  const selectedRegion = useSelector(selectTeamReadinessSelectedRegion);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const selectedTeam: SRITeamReadinessTeam = useSelector(selectedTeamSRI);
  const teamRankLoading = useSelector(selectTeamReadinessTeamRankLoading);
  const teamRankData = useSelector(selectTeamReadinessTeamRankData);
  // const filtersLoading = useSelector(selectTeamReadinessFiltersLoading);
  const regionTeams = filterTeams[selectedRegion];
  const resultType = teamRankData?.type;
  const teamRankColumns = teamRankData?.group_details?.column_details;
  const teamDetailsData = teamRankData?.group_details?.team_data;

  const xAxisCategories = teamReadinessAvgGraph
    ?.group_overview?.results?.map((val) => `${val.month_name!}-${val.year!}`);
  const scores = teamReadinessAvgGraph
    ?.group_overview?.results?.map((val) => val.score);
  const teamRank = teamReadinessAvgGraph
    ?.group_overview?.results?.map((val) => val.team_company_rank);
  const regionCategories = teamReadinessAvgGraph?.group_overview?.results && teamReadinessAvgGraph
    .group_overview.results[0]?.overview_result?.map((val) => val.month_name);
  const plotting = teamReadinessAvgGraph?.group_overview?.results?.map((val) => ({
    name: val.group_name,
    type: 'spline',
    color: val.color,
    data: val?.overview_result?.map((v) => v.score),
    rank: val?.overview_result?.map((v) => v.team_company_rank),
  }));

  const setRegion = ({ key }: { key: string }) => {
    dispatch(SRIActions.setTeamReadinessFilterRegion(key));
  };

  const setTeam = ({ key }: { key: string }) => {
    const teamData = regionTeams.find((team) => team.team_id.toString() === key);
    if (teamData) {
      dispatch(SRIActions.setTeamReadinessFilterTeam(teamData));
    }
  };

  const setUser = (userId: number) => {
    dispatch(SRIActions.setSelectedUserId(userId));
    navigate(`?userId=${userId}`);
  };

  function getFiltersObject() {
    const filters = [];
    filters.push({
      type: 'region_filter',
      name: selectedRegion,
    });
    filters.push({ type: 'team_filter', name: selectedTeam.team_name, id: selectedTeam.team_id });
    filters.push({ type: 'team_overview', ...SRIFilter });
    return filters;
  }

  useEffect(() => {
    if (SRIFilter.start_date !== '' && SRIFilter.end_date !== '' && selectedRegion !== '' && Object.keys(selectedTeam).length > 0) {
      setIsLoading(true);
      const obj: SRITabFilters = {
        filters: getFiltersObject(),
      };
      dispatch(SRIActions.getTeamReadinessBeltsGraph(obj));
      dispatch(SRIActions.getTeamReadinessAvgGraph(obj));
      dispatch(SRIActions.getTeamReadinessTeamDetails(obj));
    }
  }, [SRIFilter, selectedTeam, selectedRegion]);

  useEffect(() => {
    if (!teamReadinessAvgLoading && !teamRankLoading && !teamReadinessBeltsLoading) {
      setIsLoading(false);
    }
  }, [teamReadinessAvgLoading, teamRankLoading, teamReadinessBeltsLoading]);

  return (
    <Wrapper>
      {resultType === 'single_team' && (
        <TeamDetails
          teamRankLoading={teamRankLoading}
          teamDetailsData={teamDetailsData}
        />
      )}
      <Overlay>
        <div className="head-wrap d-flex justify-content-between mb-c-30">
          <h6 className="title mb-0">Average Score</h6>
        </div>
        {teamReadinessAvgLoading || isLoading ? <ChartSpinner />
          : (resultType === 'single_team' ? (
            <BarLineChart
              xAxis={xAxisCategories || []}
              scores={scores || []}
              teamRank={teamRank || []}
              team
              teamRankCount={teamRankData.group_details?.team_data?.total_teams_in_company || 0}
            />
          ) : (
            <MultiLineChart
              xAxis={regionCategories || []}
              data={plotting || [{}]}
            />
          )
          )}
      </Overlay>
      {resultType === 'single_team' && (
        <TeamDeatilsCards
          teamRankLoading={teamRankLoading}
          teamDetailsData={teamDetailsData}
        />
      )}
      <Overlay>
        <div className="head-wrap d-flex justify-content-between mb-c-50">
          <h6 className="title mb-0">Leaderboard Summary</h6>
          {/* <DropdownFilter menu={menu} /> */}
        </div>
        {teamReadinessBeltsLoading ? <ChartSpinner />
          : (
            <BeltChart
              data={teamReadinessBeltsGraph}
            />
          )}
      </Overlay>
      {
        teamRankLoading
          ? (
            <>
              <SkeletonCustom width="100%" height="50px" diameter="8px" margin="0 0 10px 0" />
              {[1, 2, 3, 4].map(() => <SkeletonCustom width="100%" height="111px" diameter="8px" margin="0 0 10px 0" />)}
            </>
          )
          : (
            <>
              <TeamReadinessTableHeader
                data={teamRankColumns && Object.values(teamRankColumns)}
                personImg={resultType === 'single_team' ? 'single-team' : ''}
              />
              {teamRankData?.group_details?.results?.map((team) => (
                <TeamReadinessTableBody
                  data={team}
                  keys={(teamRankColumns && Object.keys(teamRankColumns)) || ['']}
                  personImg={resultType === 'single_team' ? team?.member_data?.member_image : ''}
                  beltLevel={team?.level?.image}
                  setRegion={setRegion}
                  setTeam={setTeam}
                  setUser={setUser}
                  filterType={resultType}
                />
              ))}
            </>
          )
      }
    </Wrapper>
  );
};

export default TeamReadinessTab;
